import { useMemo } from 'react';
import { Clock, DollarSign, TrendingUp, Info, CreditCard, EyeOff } from 'lucide-react';
import { cn } from '../../../utils/cn';
import RecurringScheduleConfig from '../RecurringScheduleConfig';
import { calculateDurationMinutes, formatDuration, calculateWeeklyDuration } from '../../../utils/scheduleUtils';
import { userJobPreferencesService } from '../../../services/userJobPreferencesService';

// GST calculation helpers
const calculateGstBreakdown = (amount, gstType, gstRate) => {
  if (!amount || amount <= 0) return null;

  const rate = gstRate / 100;

  if (gstType === 'exclusive') {
    // User entered base amount, calculate GST and total
    const baseAmount = amount;
    const gstAmount = baseAmount * rate;
    const totalAmount = baseAmount + gstAmount;
    return { baseAmount, gstAmount, totalAmount };
  } else {
    // User entered total (inclusive), calculate base and GST
    const totalAmount = amount;
    const baseAmount = totalAmount / (1 + rate);
    const gstAmount = totalAmount - baseAmount;
    return { baseAmount, gstAmount, totalAmount };
  }
};

export default function SchedulePaymentsTab({
  formData,
  onChange,
  errors,
  gstRate = 10,
  userId,
  companyId
}) {
  // Calculate GST breakdowns
  const clientGstBreakdown = useMemo(() => {
    if (!formData?.clientIncludeGst) return null;
    return calculateGstBreakdown(
      parseFloat(formData?.clientChargeAmount) || 0,
      formData?.clientGstType || 'exclusive',
      gstRate
    );
  }, [formData?.clientChargeAmount, formData?.clientIncludeGst, formData?.clientGstType, gstRate]);

  const contractorGstBreakdown = useMemo(() => {
    if (!formData?.contractorIncludeGst) return null;
    return calculateGstBreakdown(
      parseFloat(formData?.paymentAmount) || 0,
      formData?.contractorGstType || 'exclusive',
      gstRate
    );
  }, [formData?.paymentAmount, formData?.contractorIncludeGst, formData?.contractorGstType, gstRate]);

  // Calculate margin using BASE amounts (excluding GST) for accurate profit margin
  const margin = useMemo(() => {
    if (!formData?.showPaymentSection) return null;

    // Get base amounts (without GST) for comparison
    let clientBaseAmount = parseFloat(formData?.clientChargeAmount) || 0;
    let contractorBaseAmount = parseFloat(formData?.paymentAmount) || 0;

    // If GST is included, use the base amount from breakdown
    if (formData?.clientIncludeGst && clientGstBreakdown) {
      clientBaseAmount = clientGstBreakdown.baseAmount;
    }
    if (formData?.contractorIncludeGst && contractorGstBreakdown) {
      contractorBaseAmount = contractorGstBreakdown.baseAmount;
    }

    if (clientBaseAmount === 0 || contractorBaseAmount === 0) return null;

    // If different payment types, can't calculate margin
    if (formData?.clientChargeType !== formData?.paymentType) return null;

    const difference = clientBaseAmount - contractorBaseAmount;
    const percentage = ((difference / clientBaseAmount) * 100).toFixed(1);

    return {
      amount: difference,
      percentage,
      isPositive: difference > 0
    };
  }, [formData?.clientChargeAmount, formData?.paymentAmount, formData?.clientChargeType, formData?.paymentType, formData?.clientIncludeGst, formData?.contractorIncludeGst, formData?.showPaymentSection, clientGstBreakdown, contractorGstBreakdown]);

  const handleJobTypeChange = (jobType) => {
    onChange?.({ jobType });

    // Reset schedule data when switching types
    if (jobType === 'one_off') {
      onChange?.({ recurringSchedule: null, recurringEndDate: null });
    } else {
      onChange?.({ scheduledDate: '', scheduledTime: '' });
    }
  };

  // Handle payment section toggle
  const handlePaymentSectionToggle = async (checked) => {
    onChange?.({ showPaymentSection: checked });

    // If user is hiding payment section, save preference for future jobs
    if (!checked && userId && companyId) {
      try {
        await userJobPreferencesService.updatePreferences(userId, companyId, {
          showPaymentSection: false
        });
      } catch (err) {
        console.warn('Failed to save payment preference:', err);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Job Type Selection */}
      <div>
        <label className="block text-sm font-medium text-white-shade-1 mb-2">
          Job Type
        </label>
        <div className="flex gap-4">
          <label
            className={cn(
              'flex-1 p-4 rounded-lg border cursor-pointer transition-colors',
              formData?.jobType === 'one_off'
                ? 'border-accent bg-accent/10'
                : 'border-border'
            )}
          >
            <input
              type="radio"
              name="jobType"
              value="one_off"
              checked={formData?.jobType === 'one_off'}
              onChange={(e) => handleJobTypeChange(e?.target?.value)}
              className="sr-only"
            />
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                formData?.jobType === 'one_off' ? 'border-accent' : 'border-gray-600'
              )}>
                {formData?.jobType === 'one_off' && (
                  <div className="w-2.5 h-2.5 rounded-full bg-accent" />
                )}
              </div>
              <div>
                <p className={cn('text-sm font-medium', formData?.jobType === 'one_off' ? 'text-accent' : 'text-text-primary')}>One-time</p>
                <p className="text-xs text-white-shade-2">Single occurrence job</p>
              </div>
            </div>
          </label>

          <label
            className={cn(
              'flex-1 p-4 rounded-lg border cursor-pointer transition-colors',
              formData?.jobType === 'recurring'
                ? 'border-accent bg-accent/10'
                : 'border-border'
            )}
          >
            <input
              type="radio"
              name="jobType"
              value="recurring"
              checked={formData?.jobType === 'recurring'}
              onChange={(e) => handleJobTypeChange(e?.target?.value)}
              className="sr-only"
            />
            <div className="flex items-center gap-3">
              <div className={cn(
                'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                formData?.jobType === 'recurring' ? 'border-accent' : 'border-gray-600'
              )}>
                {formData?.jobType === 'recurring' && (
                  <div className="w-2.5 h-2.5 rounded-full bg-accent" />
                )}
              </div>
              <div>
                <p className={cn('text-sm font-medium', formData?.jobType === 'recurring' ? 'text-accent' : 'text-text-primary')}>Recurring</p>
                <p className="text-xs text-white-shade-2">Repeats on schedule</p>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* One-time Schedule */}
      {formData?.jobType === 'one_off' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-white-shade-1 mb-1">
              Scheduled Date <span className="text-red-400">*</span>
            </label>
            <input
              type="date"
              value={formData?.scheduledDate || ''}
              onChange={(e) => onChange?.({ scheduledDate: e?.target?.value })}
              min={new Date().toISOString().split('T')[0]}
              className={cn(
                'w-full px-3 py-2 bg-white border rounded-lg text-background focus:outline-none focus:border-primary-orange',
                errors?.scheduledDate ? 'border-red-500' : 'border-gray-300'
              )}
            />
            {errors?.scheduledDate && (
              <p className="mt-1 text-xs text-red-400">{errors.scheduledDate}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-white-shade-1 mb-1">
              Scheduled Time
            </label>
            <input
              type="time"
              value={formData?.scheduledTime || ''}
              onChange={(e) => onChange?.({ scheduledTime: e?.target?.value })}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background focus:outline-none focus:border-primary-orange"
            />
          </div>
        </div>
      )}

      {/* Recurring Schedule */}
      {formData?.jobType === 'recurring' && (
        <div className="p-4 bg-surface rounded-lg border border-border space-y-4">
          <RecurringScheduleConfig
            value={formData?.recurringSchedule}
            onChange={(schedule) => onChange?.({ recurringSchedule: schedule })}
            endDate={formData?.recurringEndDate}
            onEndDateChange={(date) => onChange?.({ recurringEndDate: date })}
          />
          {errors?.recurringSchedule && (
            <p className="mt-2 text-xs text-red-400">{errors.recurringSchedule}</p>
          )}

          {/* Initial Instances to Create */}
          <div className="pt-4 border-t border-gray-600">
            <label className="block text-sm font-medium text-white-shade-1 mb-2">
              Initial Instances to Create
            </label>
            <select
              value={formData?.initialInstanceCount || 5}
              onChange={(e) => onChange?.({ initialInstanceCount: parseInt(e?.target?.value) })}
              className="w-full px-3 py-2 bg-background border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
            >
              <option value={1}>1 instance</option>
              <option value={3}>3 instances</option>
              <option value={5}>5 instances (default)</option>
              <option value={10}>10 instances</option>
              <option value={20}>20 instances</option>
            </select>
            <p className="mt-1 text-xs text-text-secondary">
              You can create more instances later from Job Management.
            </p>
          </div>
        </div>
      )}

      {/* Estimated Hours - Only show for one-off jobs */}
      {formData?.jobType === 'one_off' && (
        <div>
          <label className="block text-sm font-medium text-white-shade-1 mb-1">
            Estimated Hours
          </label>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-text-secondary" />
            <input
              type="number"
              value={formData?.estimatedHours || ''}
              onChange={(e) => onChange?.({ estimatedHours: parseFloat(e?.target?.value) || null })}
              placeholder="0"
              min="0.5"
              max="24"
              step="0.5"
              className="w-24 px-3 py-2 bg-white border border-gray-300 rounded-lg text-background focus:outline-none focus:border-primary-orange"
            />
            <span className="text-sm text-text-secondary">hours</span>
          </div>
          <p className="mt-1 text-xs text-text-secondary">
            Approximate time to complete this job
          </p>
        </div>
      )}

      {/* Duration Summary for Recurring Jobs */}
      {formData?.jobType === 'recurring' && formData?.recurringSchedule && (
        <DurationSummary schedule={formData.recurringSchedule} />
      )}

      {/* Payment Section Divider */}
      <div className="border-t border-border pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary-orange" />
            <h3 className="text-sm font-medium text-text-primary">Payment Details</h3>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData?.showPaymentSection ?? true}
              onChange={(e) => handlePaymentSectionToggle(e?.target?.checked)}
              className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
            />
            <span className="text-sm text-text-secondary">Show payment fields</span>
          </label>
        </div>

        {!formData?.showPaymentSection && (
          <div className="p-4 bg-surface rounded-lg border border-border flex items-start gap-3">
            <EyeOff className="w-5 h-5 text-text-secondary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-text-primary">Payment section hidden</p>
              <p className="text-xs text-text-secondary mt-1">
                Payment details won't be required for this job. Your preference will be saved for future jobs.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Payment Section (collapsible based on user preference) */}
      {formData?.showPaymentSection && (
        <>
          {/* Charge to Client */}
          <div className="p-4 bg-surface rounded-lg border border-border">
            <h3 className="text-sm font-medium text-text-secondary mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-accent" />
              Charge to Client
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Payment Type */}
              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-2">
                  Payment Type
                </label>
                <div className="flex gap-2">
                  <label
                    className={cn(
                      'flex-1 p-2 rounded-lg border cursor-pointer transition-colors text-center text-sm',
                      formData?.clientChargeType === 'hourly'
                        ? 'border-accent text-accent'
                        : 'border-border text-text-secondary'
                    )}
                  >
                    <input
                      type="radio"
                      name="clientChargeType"
                      value="hourly"
                      checked={formData?.clientChargeType === 'hourly'}
                      onChange={(e) => onChange?.({ clientChargeType: e?.target?.value })}
                      className="sr-only"
                    />
                    Per Hour
                  </label>
                  <label
                    className={cn(
                      'flex-1 p-2 rounded-lg border cursor-pointer transition-colors text-center text-sm',
                      formData?.clientChargeType === 'fixed'
                        ? 'border-accent text-accent'
                        : 'border-border text-text-secondary'
                    )}
                  >
                    <input
                      type="radio"
                      name="clientChargeType"
                      value="fixed"
                      checked={formData?.clientChargeType === 'fixed'}
                      onChange={(e) => onChange?.({ clientChargeType: e?.target?.value })}
                      className="sr-only"
                    />
                    Fixed
                  </label>
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-2">
                  Amount (AUD) <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">A$</span>
                  <input
                    type="number"
                    value={formData?.clientChargeAmount || ''}
                    onChange={(e) => onChange?.({ clientChargeAmount: parseFloat(e?.target?.value) || null })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className={cn(
                      'w-full pl-10 pr-3 py-2 bg-white border rounded-lg text-background focus:outline-none focus:border-accent',
                      errors?.clientChargeAmount ? 'border-red-500' : 'border-gray-300'
                    )}
                  />
                </div>
                {errors?.clientChargeAmount && (
                  <p className="mt-1 text-xs text-red-400">{errors.clientChargeAmount}</p>
                )}
              </div>
            </div>

            {/* GST Options */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-4">
                {/* Include GST Checkbox */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.clientIncludeGst ?? true}
                    onChange={(e) => onChange?.({ clientIncludeGst: e?.target?.checked })}
                    className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                  />
                  <span className="text-sm text-text-primary">Include GST</span>
                </label>

                {/* Inclusive/Exclusive Toggle - only show when GST is included */}
                {formData?.clientIncludeGst && (
                  <div className="flex gap-2">
                    <label
                      className={cn(
                        'px-3 py-1 rounded border cursor-pointer transition-colors text-xs',
                        formData?.clientGstType === 'exclusive'
                          ? 'border-accent text-accent bg-accent/10'
                          : 'border-border text-text-secondary'
                      )}
                    >
                      <input
                        type="radio"
                        name="clientGstType"
                        value="exclusive"
                        checked={formData?.clientGstType === 'exclusive'}
                        onChange={(e) => onChange?.({ clientGstType: e?.target?.value })}
                        className="sr-only"
                      />
                      Exclusive
                    </label>
                    <label
                      className={cn(
                        'px-3 py-1 rounded border cursor-pointer transition-colors text-xs',
                        formData?.clientGstType === 'inclusive'
                          ? 'border-accent text-accent bg-accent/10'
                          : 'border-border text-text-secondary'
                      )}
                    >
                      <input
                        type="radio"
                        name="clientGstType"
                        value="inclusive"
                        checked={formData?.clientGstType === 'inclusive'}
                        onChange={(e) => onChange?.({ clientGstType: e?.target?.value })}
                        className="sr-only"
                      />
                      Inclusive
                    </label>
                  </div>
                )}
              </div>

              {/* GST Breakdown Display */}
              {formData?.clientIncludeGst && clientGstBreakdown && (
                <div className="mt-3 p-3 bg-background rounded-lg border border-border">
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-text-secondary text-xs">Base Amount</p>
                      <p className="text-text-primary font-medium">
                        A$ {clientGstBreakdown.baseAmount.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-text-secondary text-xs">GST ({gstRate}%)</p>
                      <p className="text-text-primary font-medium">
                        A$ {clientGstBreakdown.gstAmount.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-text-secondary text-xs">Total</p>
                      <p className="text-accent font-medium">
                        A$ {clientGstBreakdown.totalAmount.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Pay to Contractor */}
          <div className="p-4 bg-surface rounded-lg border border-border">
            <h3 className="text-sm font-medium text-text-primary mb-4 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-accent" />
              Pay to Contractor
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Payment Type */}
              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-2">
                  Payment Type
                </label>
                <div className="flex gap-2">
                  <label
                    className={cn(
                      'flex-1 p-2 rounded-lg border cursor-pointer transition-colors text-center text-sm',
                      formData?.paymentType === 'hourly'
                        ? 'border-accent text-accent'
                        : 'border-border text-text-secondary'
                    )}
                  >
                    <input
                      type="radio"
                      name="paymentType"
                      value="hourly"
                      checked={formData?.paymentType === 'hourly'}
                      onChange={(e) => onChange?.({ paymentType: e?.target?.value })}
                      className="sr-only"
                    />
                    Per Hour
                  </label>
                  <label
                    className={cn(
                      'flex-1 p-2 rounded-lg border cursor-pointer transition-colors text-center text-sm',
                      formData?.paymentType === 'fixed'
                        ? 'border-accent text-accent'
                        : 'border-border text-text-secondary'
                    )}
                  >
                    <input
                      type="radio"
                      name="paymentType"
                      value="fixed"
                      checked={formData?.paymentType === 'fixed'}
                      onChange={(e) => onChange?.({ paymentType: e?.target?.value })}
                      className="sr-only"
                    />
                    Fixed
                  </label>
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-white-shade-1 mb-2">
                  Amount (AUD)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">A$</span>
                  <input
                    type="number"
                    value={formData?.paymentAmount || ''}
                    onChange={(e) => onChange?.({ paymentAmount: parseFloat(e?.target?.value) || null })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className={cn(
                      'w-full pl-10 pr-3 py-2 bg-white border rounded-lg text-background focus:outline-none focus:border-accent',
                      errors?.paymentAmount ? 'border-red-500' : 'border-gray-300'
                    )}
                  />
                </div>
                {errors?.paymentAmount && (
                  <p className="mt-1 text-xs text-red-400">{errors.paymentAmount}</p>
                )}
              </div>
            </div>

            {/* GST Options */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-4">
                {/* Include GST Checkbox */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.contractorIncludeGst ?? false}
                    onChange={(e) => onChange?.({ contractorIncludeGst: e?.target?.checked })}
                    className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent focus:ring-offset-0"
                  />
                  <span className="text-sm text-text-primary">Include GST</span>
                </label>

                {/* Inclusive/Exclusive Toggle - only show when GST is included */}
                {formData?.contractorIncludeGst && (
                  <div className="flex gap-2">
                    <label
                      className={cn(
                        'px-3 py-1 rounded border cursor-pointer transition-colors text-xs',
                        formData?.contractorGstType === 'exclusive'
                          ? 'border-accent text-accent bg-accent/10'
                          : 'border-border text-text-secondary'
                      )}
                    >
                      <input
                        type="radio"
                        name="contractorGstType"
                        value="exclusive"
                        checked={formData?.contractorGstType === 'exclusive'}
                        onChange={(e) => onChange?.({ contractorGstType: e?.target?.value })}
                        className="sr-only"
                      />
                      Exclusive
                    </label>
                    <label
                      className={cn(
                        'px-3 py-1 rounded border cursor-pointer transition-colors text-xs',
                        formData?.contractorGstType === 'inclusive'
                          ? 'border-accent text-accent bg-accent/10'
                          : 'border-border text-text-secondary'
                      )}
                    >
                      <input
                        type="radio"
                        name="contractorGstType"
                        value="inclusive"
                        checked={formData?.contractorGstType === 'inclusive'}
                        onChange={(e) => onChange?.({ contractorGstType: e?.target?.value })}
                        className="sr-only"
                      />
                      Inclusive
                    </label>
                  </div>
                )}
              </div>

              {/* GST Breakdown Display */}
              {formData?.contractorIncludeGst && contractorGstBreakdown && (
                <div className="mt-3 p-3 bg-background rounded-lg border border-border">
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <p className="text-text-secondary text-xs">Base Amount</p>
                      <p className="text-text-primary font-medium">
                        A$ {contractorGstBreakdown.baseAmount.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-text-secondary text-xs">GST ({gstRate}%)</p>
                      <p className="text-text-primary font-medium">
                        A$ {contractorGstBreakdown.gstAmount.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-text-secondary text-xs">Total</p>
                      <p className="text-accent font-medium">
                        A$ {contractorGstBreakdown.totalAmount.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Margin Display */}
          {margin && (
            <div className={cn(
              'p-4 rounded-lg border flex items-center gap-3',
              margin.isPositive
                ? 'bg-accent/10 border-accent/30'
                : 'bg-red-500/10 border-red-500/30'
            )}>
              <TrendingUp className={cn(
                'w-5 h-5',
                margin.isPositive ? 'text-accent' : 'text-red-400'
              )} />
              <div>
                <p className="text-sm font-medium text-text-primary">
                  Margin: AUD$ {margin.amount.toFixed(2)}
                  {formData?.paymentType === 'hourly' && '/hr'}
                  {' '}
                  <span className={margin.isPositive ? 'text-accent' : 'text-red-400'}>
                    ({margin.percentage}%)
                  </span>
                </p>
                <p className="text-xs text-white-shade-2">
                  {margin.isPositive
                    ? 'Profit per job (excl. GST)'
                    : 'Warning: Paying contractor more than charging client'}
                </p>
              </div>
            </div>
          )}

          {/* Payment Type Mismatch Warning */}
          {formData?.clientChargeType !== formData?.paymentType && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
              <Info className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-400">
                Client and contractor payment types are different. Make sure this is intentional
                (e.g., charging client fixed rate but paying contractor hourly).
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Duration summary component for recurring jobs
function DurationSummary({ schedule }) {
  if (!schedule || !schedule.days?.length) return null;

  const { pattern, startTime, endTime, customSchedule, days } = schedule;

  // Check if we have complete time range data
  const hasTimeRanges = pattern === 'fixed'
    ? (startTime && endTime)
    : (customSchedule && Object.values(customSchedule).some(d => d?.startTime && d?.endTime));

  if (!hasTimeRanges) return null;

  if (pattern === 'fixed' && startTime && endTime) {
    const duration = calculateDurationMinutes(startTime, endTime);
    const weeklyTotal = duration * days.length;

    return (
      <div className="p-3 bg-surface rounded-lg border border-border">
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-text-secondary" />
          <span className="text-text-secondary">Duration per instance:</span>
          <span className="text-accent font-medium">{formatDuration(duration)}</span>
        </div>
        <p className="text-xs text-text-secondary mt-1">
          Weekly total: {formatDuration(weeklyTotal)} ({days.length} days)
        </p>
      </div>
    );
  }

  if (pattern === 'custom' && customSchedule) {
    const weeklyTotal = calculateWeeklyDuration(schedule);

    return (
      <div className="p-3 bg-surface rounded-lg border border-border">
        <p className="text-xs text-text-secondary mb-2 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Duration per day:
        </p>
        <div className="flex flex-wrap gap-2">
          {days.map(dayKey => {
            const daySchedule = customSchedule[dayKey];
            if (!daySchedule?.startTime || !daySchedule?.endTime) return null;
            const duration = calculateDurationMinutes(daySchedule.startTime, daySchedule.endTime);
            return (
              <span key={dayKey} className="px-2 py-1 bg-surface-light rounded text-xs">
                {dayKey.charAt(0).toUpperCase() + dayKey.slice(1)}:{' '}
                <span className="text-accent font-medium">{formatDuration(duration)}</span>
              </span>
            );
          })}
        </div>
        <p className="text-xs text-text-secondary mt-2">
          Weekly total: {formatDuration(weeklyTotal)}
        </p>
      </div>
    );
  }

  return null;
}
